// Modules
import { useCallback, useEffect } from "react";
import { useRouter } from "next/router";

// Redux
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { activeFiltersSelector, setActiveFiltersAndUrlPath, sortedSelectedFiltersSelector } from "redux/filters/filtersSlice";
import { seoInformationsSelector, setSeoInfoPage } from "redux/seoInformations/seoInformationsSlice";

// Types
import {Category} from "interfaces/category";

// Utils
import {
	checkIfQueryStringExists,
	createStringFromQueryObject,
	deleteQueryStringExceptArgumentKeys,
	getCompleteUrlAsPathFromRouter,
	getUrlWithoutQueryParams
} from "utils/queryString";

// Constants
import { QueryStringPrimaryKeys } from "constants/urlEnum";
import {FORMAZIONE_PAGE} from "constants/pages";

export const useFilters = () => {
	const dispatch = useAppDispatch();
	const router = useRouter();
	const activeFilters = useAppSelector(activeFiltersSelector);
	const sortedSelectedFilters = useAppSelector(sortedSelectedFiltersSelector);
	const seoInformations = useAppSelector(seoInformationsSelector);

	const buildSeoTitle = useCallback(
		(filters: Category[]): void => {
			if (router.isReady) {
				const currentUrl = getCompleteUrlAsPathFromRouter(router.asPath.slice(1));
				const matchedSeoInfo = seoInformations.filter((seoInfo) => getUrlWithoutQueryParams(seoInfo.url) === getUrlWithoutQueryParams(currentUrl)).pop();
				if (matchedSeoInfo) {
					dispatch(setSeoInfoPage({ ...matchedSeoInfo, url: matchedSeoInfo.url }));
				} else {
					const groupedByUrlParameter: Record<string, Category[]> = {};

					filters.forEach((category) => {
						if (!groupedByUrlParameter[category.urlParameter]) {
							groupedByUrlParameter[category.urlParameter] = [];
						}
						groupedByUrlParameter[category.urlParameter].push(category);
					});

					const seoTitle = Object.values(groupedByUrlParameter)
						.map((group) => group[0]?.name)
						.filter(Boolean)
						.join(" ");

					const seoInfo = {
						url: currentUrl,
						title: seoTitle,
						h1: seoTitle
					};
					dispatch(setSeoInfoPage(seoInfo));
				}
			}
		},
		[seoInformations, router]
	);

	useEffect(() => {
		buildSeoTitle(sortedSelectedFilters);
	}, [router.asPath]);

	const clearAllFilters = useCallback(() => {
		if (router.isReady && Object.keys(router.query).length) {
			if (activeFilters.filter((activeFilter) => activeFilter.selectedTags.length > 0).length || checkIfQueryStringExists(router.query, QueryStringPrimaryKeys.SEARCH)) {
				const newQueryString = deleteQueryStringExceptArgumentKeys(router.query, [QueryStringPrimaryKeys.SHOW_NUMBER_PRODUCTS]);
				dispatch(setActiveFiltersAndUrlPath(createStringFromQueryObject(newQueryString)));
				router.push(
					{
						pathname: FORMAZIONE_PAGE,
						query: newQueryString
					},
					undefined,
					{ shallow: true }
				);
			}
		}
	}, [router, activeFilters]);

	return clearAllFilters;
};
